import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TabPanel, TabView } from 'primereact/tabview'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import { useCurrentUser } from '../../auth/useCurrentUser'
import { ROLE_IDS } from '../../auth/roles'
import DeadlineRequestsPanel from './components/DeadlineRequestsPanel'
import { useRefreshDeadlineRequests } from './hooks/useDeadlineRequests'
import { DEADLINE_REQUEST_TAB, type DeadlineRequestTab } from './types'

/** Sekme sırası — `activeIndex` ile eşlenir (TabView index tabanlı çalışır). */
const TABS: DeadlineRequestTab[] = [DEADLINE_REQUEST_TAB.MINE, DEADLINE_REQUEST_TAB.INBOX]

/**
 * **Hedef Teslim Tarihi Güncelleme Talebi** ekranı (087/US4).
 *
 * <p><b>İki sekme</b> (kullanıcı kararı 2026-08-14): **Taleplerim** (kendi girdiği talepler) ve **Onay
 * Bekleyenler** (kök aksiyonunu kendisinin oluşturduğu, yani kendisine düşen talepler). Tek listede bu
 * ikisi ayırt edilemiyordu. Sekme sunucuda kapsamı **daraltır**; ikisinin birleşimi eski tek-liste
 * kapsamının aynısıdır, dolayısıyla sekme seçmek görünürlüğü genişletmez. **Genel Müdür rolünde
 * "Taleplerim" gizlidir** (kullanıcı kararı 2026-08-17) — bkz. `showMine`.
 *
 * <p>Sekmelerin gövdesi {@link DeadlineRequestsPanel}'dedir; aynı onay kuyruğu Onaylarım
 * ekranında (131) da bu panelle gösterilir.
 *
 * <p>Bilinçli olarak **yok**: ekle/düzenle/sil (talep aksiyon detayından girilir), Excel dışa aktarma
 * (istenmedi) ve kolon sıralaması (düzen sunucuda sabit — en yeni talep üstte).
 */
export default function DeadlineRequestsPage() {
  const { t } = useTranslation()
  const { data: me } = useCurrentUser()
  const refresh = useRefreshDeadlineRequests()

  /** Aktif sekme (0 = Taleplerim, 1 = Onay Bekleyenler). */
  const [tabIndex, setTabIndex] = useState(0)

  /**
   * **"Taleplerim" sekmesi Genel Müdür rolünde gizlidir** (kullanıcı kararı 2026-08-17): GM talep
   * girmez, ekranı yalnız onay mercii olarak kullanır; kendi adına boş kalan bir sekme görmesi gerekmez.
   *
   * <p>Ayrım role göredir çünkü grant ile yapılamaz: GM hesabı aynı yetkileri Yönetici rolünden de alır
   * (sol menüdeki {@code hideForRoleIds} ile birebir aynı kozmetik istisna, tek kaynak
   * {@link ROLE_IDS}). Karşılaştırma rol **kimliğiyle** yapılır, adıyla değil: ad analistçe
   * değiştirilebilir ve o gün sekme sessizce geri görünür hâle gelirdi. Bu **yalnız görünümdür** —
   * kapsam koruması sunucudadır; gizlenen sekmenin verisi zaten GM'nin kendi girdiği taleplerdir, bir
   * yetki sınırı kaldırılmaz.
   */
  const showMine = !(me?.roleIds ?? []).includes(ROLE_IDS.GENEL_MUDUR)

  /**
   * Etkin sekme. {@link TABS} indeksleri **sabit** kalır (0 = Taleplerim, 1 = Onay Bekleyenler): gizli
   * sekme diziden çıkarılmaz, `TabPanel visible={false}` ile yalnız başlıktan düşürülür — böylece
   * `activeIndex` ↔ sekme eşlemesi rolden bağımsızdır ve indeks kaydırma hatası olmaz. GM'de etkin
   * indeks daima 1'dir.
   */
  const activeIndex = showMine ? tabIndex : 1
  const tab = TABS[activeIndex]

  /**
   * Panel `key={tab}` ile kurulur: sekme değişimi paneli yeniden kurar ve arama + durum + sayfa
   * sıfırlanır. İki sekmenin varsayılan durum filtresi farklıdır; taşınan bir filtre yeni sekmede
   * sessizce yanlış kapsam gösterirdi. Rol bilgisi (`/me`) geç geldiğinde GM'de etkin sekmenin kayması
   * da aynı yoldan doğru varsayılanla yeniden kurulur. PrimeReact TabView yalnız aktif sekmenin
   * gövdesini render ettiği için iki panel örneği aynı anda oluşmaz.
   */
  const panel = <DeadlineRequestsPanel key={tab} tab={tab} />

  return (
    <div className="admin-page deadline-requests-page">
      {/* Başlık = menü adı (anayasa: aynı i18n anahtarı). */}
      {/* Yenile BAŞLIK satırında, sağda (kullanıcı isteği 2026-08-27 — tasarım bütünlüğü). Talep
          listesinin önbelleğini tazeler; iki sekmeyi de karşılar. */}
      <PageHeader
        title={t('nav.tutanak.deadlineRequests')}
        actions={<RefreshButton onRefresh={refresh} />}
      />

      <TabView activeIndex={activeIndex} onTabChange={(e) => setTabIndex(e.index)}>
        {/* `visible={false}` sekmeyi başlıktan ve gövdeden düşürür, çocuk sırasını (indeksleri) korur. */}
        <TabPanel header={t('deadlineRequests.tab.mine')} visible={showMine}>
          {panel}
        </TabPanel>
        <TabPanel header={t('deadlineRequests.tab.inbox')}>{panel}</TabPanel>
      </TabView>
    </div>
  )
}
